import React, { FormEvent, useState, useEffect } from 'react';
import styled from 'App/styled';
import { useTheme } from 'emotion-theming';
import { FormattedMessage } from '@nykaa/intl-shared';

import Snackbar from 'components/Snackbar';
import Modal from 'shared/components/Modal';
import { NotifyMeParams } from 'store/notifyMe/types';
import { PrimaryCategories, Product } from 'store/product/types';
import { NOTIFY_ME, NOTIFY_STOCK } from 'constants/strings';
import { pushNotifyMe } from 'shared/store/notifyMe/helpers';
import { isRegisteredViaMobile } from 'shared/store/auth/helper';
import { SUCCESS_MESSAGE, FAILURE_MESSAGE } from 'store/notifyMe/constants';
import { NO_OFFER_ID, NO_OFFER_MESSAGE } from 'shared/constants';
import { isDeviceNotWeb } from 'helpers/utils/device';
import { useSelector } from 'react-redux';
import { State } from 'store/types';
import { webViewCallbacks } from '@nykaa/webview-wizard/constant';
import { mixpanelEventsForApp } from 'constants/trackingSources';
import { webViewCallbackhandler } from '@nykaa/webview-wizard';
import { useIsLoggedIn } from 'hooks/auth';

const SNACKBAR_ID = 'notify-me-prompt';
interface FormProps {
  borderColor: string;
  textColor: string;
  placeholderColor: string;
  submitTextColor: string;
}

interface WrapperProps {
  textColor: string;
}

const FormWrapper = styled.div`
  position: sticky;
  bottom: 0px;
  width: 100%;
  background: #fff;
  padding: 20px;
  ${({ theme }) => theme.typography.bodyMedium};
  color: ${({ textColor }: WrapperProps) => textColor};
  box-shadow: 1px -1px 1px rgba(0, 0, 0, 0.2);
`;

const VisuallyHidden = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`;

const Form = styled.form`
  border: 1px solid ${({ borderColor }: FormProps) => borderColor};
  display: flex;
  padding: 6px 12px;
  color: ${({ textColor }: FormProps) => textColor};
  border-radius: 3px;

  .input {
    display: inline-block;
    border: none;
    flex-grow: 1;

    ::placeholder {
      color: ${({ placeholderColor }: FormProps) => placeholderColor};
      font-weight: 500;
    }

    &:focus {
      outline: none;
    }
  }

  .submit-button {
    color: ${({ submitTextColor }: FormProps) => submitTextColor};
    border: none;
    font-weight: 600;
    letter-spacing: 1.8px;
    background: transparent;
  }
`;

interface NotifyMeProps {
  sendNotifyMe: (params: NotifyMeParams) => Promise;
  productId: string;
  userEmail: string;
  brandName?: string;
  offerPrice?: number;
  productName: string;
  onClose: Function;
  primaryCategories: PrimaryCategories;
  isLux?: number;
  offerMessage: string;
  offerId: string;
  pageSource?: string;
  trackingData?: string;
  product?: Product;
  variantType?: string;
  appTrackingKeys?: any;
  isConvSearch?: boolean;
  gptConversationId?: string;
}

function NotifyMe({
  sendNotifyMe,
  offerPrice,
  userEmail,
  brandName,
  productId,
  productName,
  onClose,
  primaryCategories,
  isLux,
  offerMessage = NO_OFFER_MESSAGE,
  offerId = NO_OFFER_ID,
  pageSource,
  trackingData,
  product,
  variantType,
  appTrackingKeys,
  isConvSearch,
  gptConversationId,
}: NotifyMeProps) {
  const [email, setEmail] = useState('');
  const [showNotifyMessage, setNotifyMeMessage] = useState(false);
  const [notifyMeError, setNotifyMeError] = useState(false);
  const [announceMessage, setAnnounceMessage] = useState('');

  const device = useSelector((state: State) => state.appReducer?.client.device);
  const isLoggedIn = useIsLoggedIn();

  const theme: Theme = useTheme();
  useEffect(() => {
    !isRegisteredViaMobile(userEmail) && setEmail(userEmail);
  }, [userEmail]);
  const handleFormSubmit = (formSubmitEvent: FormEvent) => {
    formSubmitEvent.preventDefault();
    sendNotifyMe({
      email,
      productId,
      variantName: productName,
      offerPrice,
      brandName,
    }).then((res: boolean) => {
      const { childId, discount, mrp, packSize, sku, type, name, brandName } =
        product || {};

      if (isDeviceNotWeb(device)) {
        const { interaction_location, search_term, source } =
          appTrackingKeys || {};

        const notifyMeClickedData = {
          interaction_location: isConvSearch
            ? 'searchListingPage'
            : interaction_location || '',
          product_id: productId,
          variant_product_id: sku,
          product_type: type,
          product_name: name,
          brand_name: brandName,
          category_l1_name: primaryCategories?.l1?.name,
          category_l2_name: primaryCategories?.l2?.name,
          category_l3_name: primaryCategories?.l3?.name,
          mrp_price: mrp,
          discounted_percent: discount,
          is_logged_in: !!isLoggedIn,
          source_platform: source,
          listing_type: 'searchListingPage',
          listing_value: search_term || '',
          listing_opened: `Search:query:${search_term || ''}`,
          plp_click_count:
            window?.datalayer?.plpInteractionData?.clickCount || 0,
          has_nykaa_gpt_interaction: isConvSearch || false,
          gpt_conversation_id: gptConversationId || '',
        };
        const appParams = {
          device,
          callbackFunctionName: webViewCallbacks.doMixpanel,
          callbackParams: {
            eventName: mixpanelEventsForApp.notify_me_clicked,
            params: JSON.stringify(notifyMeClickedData),
          },
        };
        webViewCallbackhandler(appParams);
      }
      pushNotifyMe({
        product,
        productId,
        productName,
        offerPrice,
        brandName,
        primaryCategories,
        isLux,
        offerMessage,
        offerId,
        pageSource,
        trackingData,
        childId,
        discount,
        mrp,
        packSize,
        variantType,
      });
      setNotifyMeMessage(true);
      setAnnounceMessage(!res ? FAILURE_MESSAGE : SUCCESS_MESSAGE);
      setNotifyMeError(!res);
      setTimeout(() => onClose(), 1000);
    });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEmail(value);
  };

  const formProps: FormProps = {
    borderColor: theme.lightGrey,
    placeholderColor: theme.lightGrey,
    submitTextColor: theme.brandPrimary,
    textColor: theme.darkGrey,
  };

  return (
    <Modal
      onHide={onClose}
      isPartial
    >
      <FormWrapper textColor={theme.darkGrey}>
        <span>
          <FormattedMessage id={NOTIFY_STOCK} />
        </span>
        <Form
          onSubmit={handleFormSubmit}
          {...formProps}
        >
          <input
            type="email"
            className="input"
            placeholder="Your Email"
            value={email}
            onChange={handleChange}
            aria-label="Your Email"
          />
          <button
            type="submit"
            className="submit-button"
          >
            <FormattedMessage id={NOTIFY_ME} />
          </button>
        </Form>
      </FormWrapper>
      <VisuallyHidden
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {announceMessage}
      </VisuallyHidden>
      {showNotifyMessage && (
        <Snackbar
          autoClose
          closeCallback={() => setNotifyMeMessage(false)}
          content={notifyMeError ? FAILURE_MESSAGE : SUCCESS_MESSAGE}
          errorType={notifyMeError}
          snackBarId={SNACKBAR_ID}
        />
      )}
    </Modal>
  );
}

export default NotifyMe;
